import React, { useState, useEffect, useRef } from 'react';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { formatCurrency } from '../../utils/formatters';

const PAYBILL_NUMBER = '4039241';

export default function RecordRepayment() {
  const { hasPermission, user } = useAuth();
  const isAdmin = String(user?.role?.name || '').trim().toLowerCase() === 'admin';

  // Form state
  const [loans, setLoans] = useState([]);
  const [filteredLoans, setFilteredLoans] = useState([]);
  const [selectedLoanId, setSelectedLoanId] = useState('');
  const [amount, setAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [paymentReference, setPaymentReference] = useState('');
  const [notes, setNotes] = useState('');

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchFocused, setSearchFocused] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const searchRef = useRef(null);
  const dropdownRef = useRef(null);

  // UI state
  const [loading, setLoading] = useState(false);
  const [fetchingLoans, setFetchingLoans] = useState(true);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  // Loan detail preview
  const [loanPreview, setLoanPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  useEffect(() => {
    fetchActiveLoans();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        searchRef.current &&
        !searchRef.current.contains(e.target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target)
      ) {
        setSearchFocused(false);
        setHighlightIndex(-1);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchActiveLoans = async () => {
    setFetchingLoans(true);
    try {
      const res = await api.get('/loans?status=active&limit=500');
      const overdueRes = await api.get('/loans?status=overdue&limit=500');
      const activeLoans = res.data?.data?.loans || [];
      const overdueLoans = overdueRes.data?.data?.loans || [];
      const allLoans = [...activeLoans, ...overdueLoans];
      setLoans(allLoans);
      setFilteredLoans(allLoans);
    } catch (err) {
      console.error('Failed to fetch loans:', err);
    } finally {
      setFetchingLoans(false);
    }
  };

  // Filter loans based on search query
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredLoans(loans);
      setHighlightIndex(-1);
      return;
    }

    const query = searchQuery.toLowerCase().trim();
    const filtered = loans.filter((loan) => {
      const firstName = (loan.client?.firstName || '').toLowerCase();
      const lastName = (loan.client?.lastName || '').toLowerCase();
      const fullName = `${firstName} ${lastName}`;
      const phone = (loan.client?.phone || '').toLowerCase();
      const nationalId = (loan.client?.nationalId || '').toLowerCase();
      const clientNumber = (loan.client?.clientNumber || '').toLowerCase();
      const loanNumber = (loan.loanNumber || '').toLowerCase();
      const productName = (loan.loanProduct?.name || '').toLowerCase();

      return (
        fullName.includes(query) ||
        firstName.includes(query) ||
        lastName.includes(query) ||
        phone.includes(query) ||
        nationalId.includes(query) ||
        clientNumber.includes(query) ||
        loanNumber.includes(query) ||
        productName.includes(query)
      );
    });

    setFilteredLoans(filtered);
    setHighlightIndex(-1);
  }, [searchQuery, loans]);

  // When a loan is selected, fetch its full details for preview
  useEffect(() => {
    if (!selectedLoanId) {
      setLoanPreview(null);
      return;
    }

    const fetchPreview = async () => {
      setPreviewLoading(true);
      try {
        const res = await api.get(`/loans/${selectedLoanId}`);
        setLoanPreview(res.data.data);
      } catch (err) {
        console.error('Failed to load loan preview:', err);
        setLoanPreview(null);
      } finally {
        setPreviewLoading(false);
      }
    };

    fetchPreview();
  }, [selectedLoanId]);

  const selectedLoan = loans.find((l) => l._id === selectedLoanId);

  const selectLoan = (loanId) => {
    setSelectedLoanId(loanId);
    const loan = loans.find((l) => l._id === loanId);
    if (loan) {
      setSearchQuery(
        `${loan.client?.firstName || ''} ${loan.client?.lastName || ''} — ${loan.loanNumber}`
      );
    }
    setSearchFocused(false);
    setHighlightIndex(-1);
  };

  const clearSelection = () => {
    setSelectedLoanId('');
    setSearchQuery('');
    setLoanPreview(null);
    setHighlightIndex(-1);
    searchRef.current?.focus();
  };

  // Keyboard navigation
  const handleSearchKeyDown = (e) => {
    const loansToShow = searchQuery.trim() ? filteredLoans : [];

    if (!searchFocused) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightIndex((prev) =>
          prev < loansToShow.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightIndex((prev) =>
          prev > 0 ? prev - 1 : loansToShow.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightIndex >= 0 && loansToShow[highlightIndex]) {
          selectLoan(loansToShow[highlightIndex]._id);
        }
        break;
      case 'Escape':
        setSearchFocused(false);
        setHighlightIndex(-1);
        break;
      default:
        break;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isAdmin) {
      setError('Only an administrator can record manual repayments.');
      return;
    }
    if (!selectedLoanId || !amount) return;

    setLoading(true);
    setResult(null);
    setError('');

    try {
      const payload = {
        loan: selectedLoanId,
        amount: parseFloat(amount),
        paymentMethod,
        paymentReference: paymentReference.trim() || undefined,
        notes: notes.trim() || `Manual repayment recorded by admin`,
      };

      const res = await api.post('/repayments', payload);
      setResult(res.data.data);

      // Reset form
      setSelectedLoanId('');
      setSearchQuery('');
      setAmount('');
      setPaymentReference('');
      setNotes('');
      setLoanPreview(null);

      // Refresh loans list
      fetchActiveLoans();
    } catch (err) {
      setError(
        err.response?.data?.errors?.[0]?.message ||
          err.response?.data?.message ||
          'Failed to record repayment'
      );
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setSelectedLoanId('');
    setSearchQuery('');
    setAmount('');
    setPaymentMethod('cash');
    setPaymentReference('');
    setNotes('');
    setLoanPreview(null);
    setResult(null);
    setError('');
  };

  const getClientInitials = (loan) => {
    const first = loan.client?.firstName || '';
    const last = loan.client?.lastName || '';
    return (first[0] || '') + (last[0] || '');
  };

  const getAvatarColor = (name) => {
    if (!name) return '#6366f1';
    const colors = [
      '#6366f1', '#8b5cf6', '#a855f7', '#d946ef',
      '#ec4899', '#f43f5e', '#ef4444', '#f97316',
      '#eab308', '#84cc16', '#22c55e', '#14b8a6',
      '#06b6d4', '#0ea5e9', '#3b82f6',
    ];
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  };

  const highlightMatch = (text, query) => {
    if (!query || !text) return text;
    const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);
    return parts.map((part, i) =>
      regex.test(part) ? (
        <mark
          key={i}
          style={{
            background: '#fef08a',
            color: '#854d0e',
            borderRadius: 2,
            padding: '0 1px',
          }}
        >
          {part}
        </mark>
      ) : (
        part
      )
    );
  };

  const inputStyle = {
    width: '100%',
    padding: '10px 12px',
    borderRadius: '8px',
    border: '1px solid #d1d5db',
    fontSize: '14px',
    outline: 'none',
    background: '#fff',
  };

  if (!isAdmin) {
    return (
      <div className="card p-4" style={{ maxWidth: 700, margin: '24px auto', textAlign: 'center' }}>
        <h3>Access Restricted</h3>
        <p>Only an administrator can submit and record manual repayments.</p>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '700px', margin: '0 auto' }}>
      <div className="flex justify-between items-center mb-3">
        <h3>📋 Record Manual Repayment</h3>
      </div>

      {/* Info Banner */}
      <div
        style={{
          background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
          border: '1px solid #bfdbfe',
          borderRadius: 10,
          padding: 14,
          marginBottom: 16,
          fontSize: 13,
          color: '#1e40af',
        }}
      >
        <strong>📋 Manual Repayment Entry:</strong> Use this form to record
        cash, bank, or mobile-money payments received at the counter. Interest
        is calculated{' '}
        <strong>in real-time based on actual days used</strong>.
        <br />
        <span
          style={{ fontSize: 12, color: '#3b82f6', marginTop: 4, display: 'block' }}
        >
          💡 M-Pesa Paybill payments are <strong>auto-detected</strong> — no
          manual entry needed. Paybill: <strong>{PAYBILL_NUMBER}</strong>,
          Account: National ID
        </span>
      </div>

      {/* ═══════════════ SUCCESS RESULT ═══════════════ */}
      {result && (
        <div
          style={{
            background: result.loanCleared
              ? 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)'
              : '#f0fdf4',
            border: `2px solid ${result.loanCleared ? '#10b981' : '#86efac'}`,
            borderRadius: 12,
            padding: 20,
            marginBottom: 20,
          }}
        >
          <h4 style={{ color: '#065f46', margin: '0 0 12px 0' }}>
            ✅ Repayment Recorded Successfully
          </h4>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 12,
              fontSize: 14,
            }}
          >
            <div>
              <div style={{ color: '#64748b', fontSize: 12 }}>Client</div>
              <div style={{ fontWeight: 600 }}>{result.client?.name}</div>
              <div style={{ fontSize: 12, color: '#64748b' }}>
                ID: {result.client?.nationalId}
              </div>
            </div>
            <div>
              <div style={{ color: '#64748b', fontSize: 12 }}>Loan</div>
              <div style={{ fontWeight: 600, fontFamily: 'monospace' }}>
                {result.loan?.loanNumber}
              </div>
            </div>
            <div>
              <div style={{ color: '#64748b', fontSize: 12 }}>
                Amount Applied
              </div>
              <div style={{ fontWeight: 700, fontSize: 18, color: '#059669' }}>
                {formatCurrency(
                  result.appliedAmount || result.repayment?.amount
                )}
              </div>
            </div>
            <div>
              <div style={{ color: '#64748b', fontSize: 12 }}>
                Remaining Balance
              </div>
              <div
                style={{
                  fontWeight: 700,
                  fontSize: 18,
                  color:
                    (result.remainingBalance ||
                      result.loan?.totalOutstanding ||
                      0) > 0
                      ? '#dc2626'
                      : '#059669',
                }}
              >
                {formatCurrency(
                  result.remainingBalance ||
                    result.loan?.totalOutstanding ||
                    0
                )}
              </div>
            </div>
          </div>

          {/* Payment Breakdown */}
          {result.breakdown && (
            <div
              style={{
                marginTop: 12,
                padding: 10,
                background: '#fff',
                borderRadius: 6,
                fontSize: 13,
                border: '1px solid #d1fae5',
              }}
            >
              <div
                style={{
                  fontWeight: 600,
                  marginBottom: 6,
                  fontSize: 12,
                  color: '#64748b',
                }}
              >
                PAYMENT BREAKDOWN
              </div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginBottom: 4,
                }}
              >
                <span>Principal:</span>
                <span style={{ fontWeight: 600 }}>
                  {formatCurrency(result.breakdown.principalAmount)}
                </span>
              </div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginBottom: 4,
                }}
              >
                <span>
                  Interest ({result.breakdown.interestRateApplied}% —{' '}
                  {result.breakdown.interestDaysUsed} actual days):
                </span>
                <span style={{ fontWeight: 600, color: '#f59e0b' }}>
                  {formatCurrency(result.breakdown.interestAmount)}
                </span>
              </div>
              {(result.breakdown.penaltyAmount || 0) > 0 && (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginBottom: 4,
                  }}
                >
                  <span>Penalty:</span>
                  <span style={{ fontWeight: 600, color: '#dc2626' }}>
                    {formatCurrency(result.breakdown.penaltyAmount)}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Loan Cleared Banner */}
          {result.loanCleared && (
            <div
              style={{
                marginTop: 12,
                padding: 14,
                background: '#fff',
                borderRadius: 8,
                border: '2px solid #10b981',
                textAlign: 'center',
              }}
            >
              <div style={{ fontSize: 24, marginBottom: 4 }}>🎉</div>
              <div
                style={{ fontWeight: 700, fontSize: 16, color: '#065f46' }}
              >
                LOAN FULLY CLEARED!
              </div>
              <div style={{ fontSize: 13, color: '#047857', marginTop: 4 }}>
                {result.client?.name} is now{' '}
                <strong>eligible to apply for a new loan</strong>.
              </div>
            </div>
          )}

          {/* Overpayment Warning */}
          {(result.overpayment || 0) > 0 && (
            <div
              style={{
                marginTop: 12,
                padding: 10,
                background: '#fffbeb',
                border: '1px solid #fcd34d',
                borderRadius: 6,
                fontSize: 13,
                color: '#92400e',
              }}
            >
              ⚠️ <strong>Overpayment:</strong> KES{' '}
              {result.overpayment.toFixed(2)} was not applied (exceeds
              outstanding balance). Please arrange a refund or credit to the
              client's next loan.
            </div>
          )}

          <div className="flex gap-2 mt-3">
            <button className="btn btn-primary" onClick={resetForm}>
              📋 Record Another Payment
            </button>
          </div>
        </div>
      )}

      {/* ═══════════════ ERROR ═══════════════ */}
      {error && (
        <div
          style={{
            background: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: 8,
            padding: 14,
            marginBottom: 16,
            color: '#dc2626',
            fontSize: 14,
          }}
        >
          ❌ {error}
        </div>
      )}

      {/* ═══════════════ FORM ═══════════════ */}
      {!result && (
        <form onSubmit={handleSubmit}>
          <div className="card p-3">
            {/* Step 1: Search & Select Loan */}
            <h5
              style={{
                margin: '0 0 12px 0',
                color: '#1e40af',
                fontSize: 14,
              }}
            >
              Step 1: Search & Select Client Loan
            </h5>

            <div className="form-group mb-2" style={{ position: 'relative' }}>
              <label style={{ fontWeight: 600, marginBottom: 6, display: 'block' }}>
                Search by Name, Phone, ID, or Loan # *
              </label>

              {/* Search Input with clear button */}
              <div style={{ position: 'relative' }} ref={searchRef}>
                <span
                  style={{
                    position: 'absolute',
                    left: 12,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: '#94a3b8',
                    fontSize: 16,
                    pointerEvents: 'none',
                  }}
                >
                  🔍
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder={
                    fetchingLoans
                      ? 'Loading loans...'
                      : 'Type client name, phone number, National ID, or loan number...'
                  }
                  value={searchQuery}
                  onChange={(e) => {
                    // If a loan was selected and user types, clear selection
                    if (selectedLoanId) {
                      setSelectedLoanId('');
                      setLoanPreview(null);
                    }
                    setSearchQuery(e.target.value);
                  }}
                  onFocus={() => setSearchFocused(true)}
                  onKeyDown={handleSearchKeyDown}
                  disabled={fetchingLoans}
                  required={!selectedLoanId}
                  style={{
                    ...inputStyle,
                    paddingLeft: 40,
                    paddingRight: selectedLoanId ? 36 : 12,
                    padding: '12px 12px 12px 40px',
                    borderColor: searchFocused ? '#3b82f6' : '#d1d5db',
                    boxShadow: searchFocused
                      ? '0 0 0 3px rgba(59, 130, 246, 0.15)'
                      : 'none',
                    transition: 'all 0.15s ease',
                  }}
                />
                {/* Clear button */}
                {searchQuery && (
                  <button
                    type="button"
                    onClick={clearSelection}
                    style={{
                      position: 'absolute',
                      right: 8,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: 18,
                      color: '#94a3b8',
                      padding: '2px 6px',
                      borderRadius: 4,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                    onMouseEnter={(e) => (e.target.style.color = '#475569')}
                    onMouseLeave={(e) => (e.target.style.color = '#94a3b8')}
                  >
                    ✕
                  </button>
                )}
              </div>

              {/* Search hint */}
              {!selectedLoanId && !searchQuery && !fetchingLoans && (
                <div
                  style={{
                    fontSize: 11,
                    color: '#94a3b8',
                    marginTop: 6,
                    display: 'flex',
                    gap: 12,
                    flexWrap: 'wrap',
                  }}
                >
                  <span>💡 Try:</span>
                  <span
                    style={{
                      background: '#f1f5f9',
                      padding: '1px 8px',
                      borderRadius: 10,
                      cursor: 'pointer',
                    }}
                    onClick={() => setSearchQuery('John')}
                  >
                    Name
                  </span>
                  <span
                    style={{
                      background: '#f1f5f9',
                      padding: '1px 8px',
                      borderRadius: 10,
                      cursor: 'pointer',
                    }}
                    onClick={() => setSearchQuery('07')}
                  >
                    Phone
                  </span>
                  <span
                    style={{
                      background: '#f1f5f9',
                      padding: '1px 8px',
                      borderRadius: 10,
                      cursor: 'pointer',
                    }}
                    onClick={() => setSearchQuery('LN')}
                  >
                    Loan #
                  </span>
                  <span
                    style={{
                      background: '#f1f5f9',
                      padding: '1px 8px',
                      borderRadius: 10,
                      cursor: 'pointer',
                    }}
                    onClick={() => setSearchQuery('CL')}
                  >
                    Client ID
                  </span>
                </div>
              )}

              {/* Selected loan indicator */}
              {selectedLoanId && (
                <div
                  style={{
                    marginTop: 6,
                    fontSize: 12,
                    color: '#059669',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                  }}
                >
                  ✅ Loan selected —{' '}
                  <button
                    type="button"
                    onClick={clearSelection}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#3b82f6',
                      cursor: 'pointer',
                      textDecoration: 'underline',
                      fontSize: 12,
                      padding: 0,
                    }}
                  >
                    change
                  </button>
                </div>
              )}

              {/* Dropdown Results */}
              {searchFocused &&
                searchQuery.trim() &&
                !selectedLoanId &&
                !fetchingLoans && (
                  <div
                    ref={dropdownRef}
                    style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      zIndex: 50,
                      background: '#fff',
                      border: '1px solid #e2e8f0',
                      borderRadius: 10,
                      boxShadow:
                        '0 10px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.1)',
                      maxHeight: 360,
                      overflowY: 'auto',
                      marginTop: 4,
                    }}
                  >
                    {filteredLoans.length === 0 ? (
                      <div
                        style={{
                          padding: '20px 16px',
                          textAlign: 'center',
                          color: '#94a3b8',
                        }}
                      >
                        <div style={{ fontSize: 28, marginBottom: 8 }}>🔍</div>
                        <div style={{ fontWeight: 500, fontSize: 14, color: '#64748b' }}>
                          No loans found
                        </div>
                        <div style={{ fontSize: 12, marginTop: 4 }}>
                          Try a different search term
                        </div>
                      </div>
                    ) : (
                      <>
                        <div
                          style={{
                            padding: '8px 14px',
                            fontSize: 11,
                            color: '#94a3b8',
                            fontWeight: 600,
                            borderBottom: '1px solid #f1f5f9',
                            position: 'sticky',
                            top: 0,
                            background: '#fff',
                            zIndex: 1,
                            borderRadius: '10px 10px 0 0',
                          }}
                        >
                          {filteredLoans.length} loan
                          {filteredLoans.length !== 1 ? 's' : ''} found
                          {filteredLoans.length > 50 && ' (showing first 50)'}
                        </div>
                        {filteredLoans.slice(0, 50).map((loan, idx) => (
                          <div
                            key={loan._id}
                            onClick={() => selectLoan(loan._id)}
                            onMouseEnter={() => setHighlightIndex(idx)}
                            style={{
                              padding: '10px 14px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 12,
                              borderBottom:
                                idx < filteredLoans.slice(0, 50).length - 1
                                  ? '1px solid #f8fafc'
                                  : 'none',
                              background:
                                idx === highlightIndex
                                  ? '#eff6ff'
                                  : 'transparent',
                              transition: 'background 0.1s ease',
                              borderRadius:
                                idx === 0
                                  ? '0'
                                  : idx ===
                                      filteredLoans.slice(0, 50).length - 1
                                    ? '0 0 10px 10px'
                                    : '0',
                            }}
                          >
                            {/* Avatar */}
                            <div
                              style={{
                                width: 38,
                                height: 38,
                                borderRadius: '50%',
                                background: getAvatarColor(
                                  `${loan.client?.firstName || ''} ${loan.client?.lastName || ''}`
                                ),
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#fff',
                                fontWeight: 700,
                                fontSize: 13,
                                flexShrink: 0,
                              }}
                            >
                              {getClientInitials(loan)}
                            </div>

                            {/* Details */}
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div
                                style={{
                                  fontWeight: 600,
                                  fontSize: 13,
                                  color: '#1e293b',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 8,
                                }}
                              >
                                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {highlightMatch(
                                    `${loan.client?.firstName || ''} ${loan.client?.lastName || ''}`.trim(),
                                    searchQuery
                                  )}
                                </span>
                                {loan.status === 'overdue' && (
                                  <span
                                    style={{
                                      background: '#fef2f2',
                                      color: '#dc2626',
                                      fontSize: 10,
                                      padding: '1px 6px',
                                      borderRadius: 10,
                                      fontWeight: 600,
                                      flexShrink: 0,
                                    }}
                                  >
                                    ⚠️ {loan.daysOverdue}d overdue
                                  </span>
                                )}
                              </div>
                              <div
                                style={{
                                  fontSize: 11,
                                  color: '#64748b',
                                  marginTop: 2,
                                  display: 'flex',
                                  gap: 10,
                                  flexWrap: 'wrap',
                                }}
                              >
                                <span
                                  style={{
                                    fontFamily: 'monospace',
                                    color: '#475569',
                                  }}
                                >
                                  {highlightMatch(loan.loanNumber, searchQuery)}
                                </span>
                                <span>📱 {loan.client?.phone || '—'}</span>
                                <span>
                                  🪪{' '}
                                  {highlightMatch(
                                    loan.client?.clientNumber || loan.client?.nationalId || '—',
                                    searchQuery
                                  )}
                                </span>
                              </div>
                            </div>

                            {/* Amount */}
                            <div
                              style={{
                                textAlign: 'right',
                                flexShrink: 0,
                              }}
                            >
                              <div
                                style={{
                                  fontWeight: 700,
                                  fontSize: 13,
                                  color: '#dc2626',
                                }}
                              >
                                {formatCurrency(loan.totalOutstanding)}
                              </div>
                              <div
                                style={{
                                  fontSize: 10,
                                  color: '#94a3b8',
                                }}
                              >
                                {loan.loanProduct?.name || 'Loan'}
                              </div>
                            </div>
                          </div>
                        ))}
                        {filteredLoans.length > 50 && (
                          <div
                            style={{
                              padding: '10px 14px',
                              textAlign: 'center',
                              fontSize: 12,
                              color: '#94a3b8',
                              background: '#f8fafc',
                              borderRadius: '0 0 10px 10px',
                            }}
                          >
                            Type more to narrow down results ({filteredLoans.length - 50} more)
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}

              {/* Hidden select for form validation */}
              <select
                required
                value={selectedLoanId}
                onChange={() => {}}
                style={{
                  position: 'absolute',
                  width: 1,
                  height: 1,
                  overflow: 'hidden',
                  clip: 'rect(0, 0, 0, 0)',
                  whiteSpace: 'nowrap',
                  border: 0,
                }}
                tabIndex={-1}
                aria-hidden="true"
              >
                <option value="" disabled>
                  Select a loan
                </option>
                {loans.map((l) => (
                  <option key={l._id} value={l._id}>
                    {l.loanNumber}
                  </option>
                ))}
              </select>
            </div>

            {loans.length === 0 && !fetchingLoans && (
              <p className="text-xs text-muted mt-1">
                No active or overdue loans found.
              </p>
            )}

            {/* Loan Preview */}
            {previewLoading && (
              <div
                style={{
                  padding: 20,
                  textAlign: 'center',
                  color: '#94a3b8',
                }}
              >
                Loading loan details...
              </div>
            )}

            {loanPreview && !previewLoading && (
              <div
                style={{
                  background: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: 8,
                  padding: 14,
                  marginBottom: 16,
                }}
              >
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: 12,
                    fontSize: 13,
                  }}
                >
                  <div>
                    <div style={{ color: '#64748b', fontSize: 11 }}>Client</div>
                    <div style={{ fontWeight: 600 }}>
                      {loanPreview.client?.firstName}{' '}
                      {loanPreview.client?.lastName}
                    </div>
                    <div style={{ color: '#64748b', fontSize: 12 }}>
                      {loanPreview.client?.phone} | ID:{' '}
                      {loanPreview.client?.clientNumber}
                    </div>
                  </div>
                  <div>
                    <div style={{ color: '#64748b', fontSize: 11 }}>Product</div>
                    <div style={{ fontWeight: 600 }}>
                      {loanPreview.loanProduct?.name}
                    </div>
                  </div>
                </div>

                {/* Balance Cards */}
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(4, 1fr)',
                    gap: 8,
                    marginTop: 12,
                  }}
                >
                  <div
                    style={{
                      textAlign: 'center',
                      background: '#fff',
                      padding: 8,
                      borderRadius: 6,
                      border: '1px solid #e2e8f0',
                    }}
                  >
                    <div style={{ fontSize: 10, color: '#64748b' }}>
                      Total Owed
                    </div>
                    <div
                      style={{
                        fontSize: 15,
                        fontWeight: 700,
                        color: '#dc2626',
                      }}
                    >
                      {formatCurrency(
                        loanPreview.interestSummary?.totalAmountOwed ||
                          loanPreview.totalOutstanding
                      )}
                    </div>
                  </div>
                  <div
                    style={{
                      textAlign: 'center',
                      background: '#fff',
                      padding: 8,
                      borderRadius: 6,
                      border: '1px solid #e2e8f0',
                    }}
                  >
                    <div style={{ fontSize: 10, color: '#64748b' }}>
                      Principal
                    </div>
                    <div style={{ fontSize: 14, fontWeight: 600 }}>
                      {formatCurrency(
                        loanPreview.interestSummary?.outstandingPrincipal ||
                          loanPreview.outstandingPrincipal
                      )}
                    </div>
                  </div>
                  <div
                    style={{
                      textAlign: 'center',
                      background: '#fff',
                      padding: 8,
                      borderRadius: 6,
                      border: '1px solid #e2e8f0',
                    }}
                  >
                    <div style={{ fontSize: 10, color: '#64748b' }}>
                      Interest
                    </div>
                    <div
                      style={{
                        fontSize: 14,
                        fontWeight: 600,
                        color: '#f59e0b',
                      }}
                    >
                      {formatCurrency(
                        loanPreview.interestSummary?.outstandingInterest ||
                          loanPreview.outstandingInterest
                      )}
                    </div>
                    {loanPreview.interestSummary?.currentInterestPercent && (
                      <div style={{ fontSize: 10, color: '#f59e0b' }}>
                        {loanPreview.interestSummary.currentInterestPercent}% (
                        {loanPreview.interestSummary.daysSinceDisbursement}d)
                      </div>
                    )}
                  </div>
                  <div
                    style={{
                      textAlign: 'center',
                      background: '#fff',
                      padding: 8,
                      borderRadius: 6,
                      border: '1px solid #e2e8f0',
                    }}
                  >
                    <div style={{ fontSize: 10, color: '#64748b' }}>
                      Penalty
                    </div>
                    <div
                      style={{
                        fontSize: 14,
                        fontWeight: 600,
                        color:
                          (loanPreview.interestSummary?.outstandingPenalty ||
                            loanPreview.outstandingPenalty ||
                            0) > 0
                            ? '#dc2626'
                            : '#94a3b8',
                      }}
                    >
                      {formatCurrency(
                        loanPreview.interestSummary?.outstandingPenalty ||
                          loanPreview.outstandingPenalty ||
                          0
                      )}
                    </div>
                    {loanPreview.interestSummary?.isOverdue && (
                      <div style={{ fontSize: 10, color: '#dc2626' }}>
                        {loanPreview.interestSummary.daysOverdue}d overdue
                      </div>
                    )}
                  </div>
                </div>

                {/* Early repayment note */}
                {loanPreview.interestSummary &&
                  !loanPreview.interestSummary.isOverdue && (
                    <div
                      style={{
                        marginTop: 8,
                        padding: '6px 10px',
                        background: '#fffbeb',
                        borderRadius: 4,
                        fontSize: 11,
                        color: '#92400e',
                      }}
                    >
                      💡 Interest accrues daily based on actual days used.
                      Current rate:{' '}
                      <strong>
                        {loanPreview.interestSummary.currentInterestPercent}%
                      </strong>{' '}
                      (
                      {loanPreview.interestSummary.daysSinceDisbursement} of{' '}
                      {loanPreview.interestSummary.totalLoanDays} days). Early
                      repayment saves money!
                    </div>
                  )}

                {/* M-Pesa Paybill info for this client */}
                <div
                  style={{
                    marginTop: 8,
                    padding: '8px 12px',
                    background: '#ecfdf5',
                    borderRadius: 6,
                    fontSize: 12,
                    color: '#065f46',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                  }}
                >
                  📱 M-Pesa auto-pay: Paybill{' '}
                  <strong>{PAYBILL_NUMBER}</strong> → Account:{' '}
                  <strong>
                    {loanPreview.client?.clientNumber || 'National ID'}
                  </strong>
                  <span
                    style={{ color: '#059669', fontWeight: 600 }}
                  >
                    (auto-detected, no manual entry needed)
                  </span>
                </div>
              </div>
            )}

            {/* Step 2: Payment Details */}
            <h5
              style={{
                margin: '16px 0 12px 0',
                color: '#1e40af',
                fontSize: 14,
              }}
            >
              Step 2: Payment Details
            </h5>

            <div className="form-group mb-2">
              <label style={{ fontWeight: 600 }}>Amount (KES) *</label>
              <input
                type="number"
                className="form-control"
                placeholder="Enter payment amount"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                required
                min="1"
                step="0.01"
                style={{ ...inputStyle, fontSize: '16px', padding: '12px' }}
              />
              {loanPreview && amount && (
                <div className="text-xs text-muted mt-1">
                  Outstanding:{' '}
                  {formatCurrency(
                    loanPreview.interestSummary?.totalAmountOwed ||
                      loanPreview.totalOutstanding
                  )}
                  {parseFloat(amount) >=
                  (loanPreview.interestSummary?.totalAmountOwed ||
                    loanPreview.totalOutstanding ||
                    0)
                    ? ' ✅ This will fully clear the loan!'
                    : ` | Remaining after payment: ${formatCurrency(
                        Math.max(
                          0,
                          (loanPreview.interestSummary?.totalAmountOwed ||
                            loanPreview.totalOutstanding ||
                            0) - parseFloat(amount)
                        )
                      )}`}
                </div>
              )}
            </div>

            <div className="grid grid-2 gap-2 mb-2">
              <div className="form-group">
                <label>Payment Method *</label>
                <select
                  className="form-control"
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  required
                >
                  <option value="cash">💵 Cash</option>
                  <option value="mobile-money">📱 Mobile Money (M-Pesa)</option>
                  <option value="bank-transfer">🏦 Bank Transfer</option>
                  <option value="cheque">📝 Cheque</option>
                  <option value="card">💳 Card</option>
                </select>
              </div>
              <div className="form-group">
                <label>Reference / Receipt #</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="M-Pesa code, receipt number..."
                  value={paymentReference}
                  onChange={(e) => setPaymentReference(e.target.value)}
                />
              </div>
            </div>

            <div className="form-group mb-3">
              <label>Notes</label>
              <textarea
                className="form-control"
                rows={2}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Any additional notes about this payment..."
              />
            </div>

            {/* Submit */}
            <button
              type="submit"
              className="btn btn-primary w-full"
              disabled={loading || !selectedLoanId || !amount}
              style={{ padding: '12px', fontSize: 15, fontWeight: 600 }}
            >
              {loading
                ? '⏳ Recording Payment...'
                : '💾 Record Manual Repayment'}
            </button>
          </div>
        </form>
      )}

      {/* Dynamic Interest Info Box */}
      <div
        style={{
          marginTop: 16,
          background: '#f8fafc',
          border: '1px solid #e2e8f0',
          borderRadius: 10,
          padding: 14,
          fontSize: 12,
          color: '#475569',
        }}
      >
        <h5
          style={{ margin: '0 0 8px 0', fontSize: 13, color: '#1e40af' }}
        >
          📊 How Interest is Calculated (Dynamic — Based on Actual Days Used)
        </h5>
        <div style={{ lineHeight: 1.7 }}>
          <strong>Interest rates:</strong>
          <br />
          • Days 1–14: <strong>14%</strong> (minimum base rate)
          <br />
          • Days 15–30: <strong>Rate = day number</strong> (Day 16 = 16%, Day
          22 = 22%)
          <br />
          • Days 31+: <strong>Capped at 30%</strong>
          <br />
          <strong>Early repayment saves money!</strong> A 20-day loan repaid on
          Day 16 = 16% (not 20%).
          <br />
          <strong>Late penalties:</strong> ≤15 days overdue = 5%, &gt;15 days
          = 15% of outstanding balance.
          <br />
          <strong>Payment allocation:</strong> Penalties → Interest → Principal.
        </div>
      </div>
    </div>
  );
}